/**
 * Media gallery rail card — recent site photos and camera images.
 *
 * Shows a thumbnail strip of the four most recent binary files for a site,
 * with a count summary and a link to the full media view. Rendered in the
 * context rail of the site dashboard alongside StructureCard and UnitCard.
 * Reads from the media summary seeded by the dashboard bootstrap thunk and
 * kept live by the binaryfile socket handler.
 *
 * Contains:
 * - MediaGalleryCard — rail card component (default export)
 */
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { Box, ButtonBase, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import BasicDashboardCard from '@sophocles/components/BasicDashboardCard';
import { selectMediaSummaryBySite } from '@sophocles/features/media/slice';

const THUMBNAIL_SIZE = 56;
const MAX_THUMBNAILS = 4;

const thumbnailSx = {
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
  flexShrink: 0,
  bgcolor: 'var(--color-gray-100)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const imgSx = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

/**
 * @param {{ loading: boolean, onMobile: boolean, params: object }} props
 */
function MediaGalleryCard({ loading, onMobile, params }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const siteId = params?.site_id ? Number(params.site_id) : null;
  const { photoCount, imageCount, recent } = useSelector(selectMediaSummaryBySite(siteId));

  const hasMedia = photoCount > 0 || imageCount > 0;
  if (!loading && !hasMedia) return null;

  const thumbnails = (recent ?? []).slice(0, MAX_THUMBNAILS);

  const mediaBasePath = `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/media`;

  const handleThumbnailClick = fileType => {
    const tab = fileType === 'cic-image' ? 'images' : 'photos';
    navigate(`${mediaBasePath}?tab=${tab}`);
  };

  const handleViewAll = () => {
    navigate(`${mediaBasePath}?tab=photos`);
  };

  const countLabel =
    photoCount > 0 && imageCount > 0
      ? t('media.summaryBoth', '{{photoCount}} photos · {{imageCount}} images', { photoCount, imageCount })
      : photoCount > 0
        ? t('media.summaryPhotos', '{{count}} photo', { count: photoCount, defaultValue_plural: '{{count}} photos' })
        : t('media.summaryImages', '{{count}} image', { count: imageCount, defaultValue_plural: '{{count}} images' });

  return (
    <BasicDashboardCard
      loading={loading}
      title={t('media.galleryCardTitle', 'Media')}
      actions={
        <ButtonBase onClick={handleViewAll} sx={{ borderRadius: 'var(--radius-sm)', px: 0.5, py: 0.25 }}>
          <Typography variant="caption" color="primary">
            {t('media.viewAll', 'View all')}
          </Typography>
        </ButtonBase>
      }
    >
      <Box sx={{ px: 'var(--space-3)', pb: 'var(--space-2)' }}>
        {thumbnails.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', mb: 'var(--space-2)' }}>
            {thumbnails.map(item => (
              <ButtonBase
                key={item.guid}
                onClick={() => handleThumbnailClick(item.file_type)}
                sx={{ ...thumbnailSx, borderRadius: 'var(--radius-sm)' }}
                aria-label={t('media.viewFileAriaLabel', 'View media file')}
              >
                {item.file_url ? (
                  <Box component="img" src={item.file_url} alt="" sx={imgSx} loading="lazy" />
                ) : (
                  <PhotoLibraryOutlinedIcon sx={{ color: 'text.disabled', fontSize: 24 }} />
                )}
              </ButtonBase>
            ))}
          </Box>
        ) : null}
        <Typography variant="caption" color="text.secondary">
          {countLabel}
        </Typography>
      </Box>
    </BasicDashboardCard>
  );
}

MediaGalleryCard.propTypes = {
  loading: PropTypes.bool,
  onMobile: PropTypes.bool,
  params: PropTypes.shape({
    cust_id: PropTypes.string,
    sitegroup_id: PropTypes.string,
    site_id: PropTypes.string,
  }).isRequired,
};

export default MediaGalleryCard;
